import React, { useMemo } from 'react';
import { Header, Page, SearchBar, Toolbox, useOnScrollDown } from 'redesign';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import { MonthDateControl } from 'shared/components/date-control-toolbox/month-date-control/month-date-control';
import { DateTime } from 'luxon';
import { useDebounce } from 'shared/hooks/use-debounce';
import { Box, styled } from '@mui/material';
import { RenderUnsettledUI } from 'shared/components';
import { useTranslation } from 'react-i18next';
import { AvailabilityCalendarGrid } from 'shared/components/availability/availability-calendar-grid';
import { useGetAvailableWorkers } from 'ogp/components/views/availability/hooks/use-get-workers-availability';
import {
  AvailabilityPageParamsProvider,
  useAvailabilityPageParamsContext,
} from '../providers/availability-page-params-provider';

export const AvailabilityPageDataProvider = () => (
  <AvailabilityPageParamsProvider>
    <AvailabilityPage />
  </AvailabilityPageParamsProvider>
);

export const AvailabilityPage = () => {
  const { t } = useTranslation('all-platforms-common');
  const { filters, setFilters } = useAvailabilityPageParamsContext();

  const debouncedWorkerSearchValue = useDebounce(filters.search, 300);

  //May later support different modes, for now we hardcode to month
  const calendarMode = 'month';

  const query = useGetAvailableWorkers({
    search: debouncedWorkerSearchValue,
    fromDate: filters.date ?? DateTime.now(),
    pageSizeParam: DEFAULT_PAGE_SIZE,
  });

  const flatData = useMemo(() => {
    const data = query.data?.pages.flatMap((data) => data.items) ?? [];
    return data;
  }, [query.data]);

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: query,
    flatData,
  });

  const selectedDate = filters.date ?? DateTime.now();

  const handleChangeDate = (newDate: DateTime) => {
    setFilters({ date: newDate });
  };

  const handleChangeSearch = (search: string) => {
    setFilters({ search });
  };

  return (
    <Page
      header={
        <FlexBox>
          <Box>
            <Header titleText={t('availability_page.title')} />
          </Box>
          <CenteredBox>
            <MonthDateControl
              date={selectedDate}
              onChange={handleChangeDate}
              onToday={() => handleChangeDate(DateTime.now())}
              todayPosition="right"
            />
          </CenteredBox>
        </FlexBox>
      }
      toolbox={
        <Toolbox>
          <SearchBar
            onChange={handleChangeSearch}
            initialValue={filters.search}
            placeholder="Filter workers"
          />
        </Toolbox>
      }
    >
      <StyledCalendarWrapper>
        {query.status === 'success' ? (
          <AvailabilityCalendarGrid
            flatData={flatData}
            selectedDate={selectedDate}
            calendarMode={calendarMode}
            onScroll={(e) => fetchIfBottomScrolled(e.target as HTMLDivElement)}
            handleAvailabilityEditClick={() => {}}
          />
        ) : (
          RenderUnsettledUI({ data: query })
        )}
      </StyledCalendarWrapper>
    </Page>
  );
};

const FlexBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const CenteredBox = styled(Box)(() => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
}));

const StyledCalendarWrapper = styled(Box)({
  height: '100%',
  width: '100%',
});
